<template>
    <div class="foodcon_page" :class="{ 'is-loading': !isReady }">
        <!-- 로딩 상태: 회원 확인 중 -->
        <div v-if="!isReady" class="foodcon_loading">
            <div class="foodcon_spinner"></div>
            <p class="foodcon_loading_text">페이지 이동 중입니다...</p>
            <p class="foodcon_loading_sub">잠시만 기다려주세요.</p>
        </div>

        <!-- 로그인 화면: 회원 확인 완료 후 미가입자만 표시 -->
        <div v-else class="foodcon_card">
            <div class="foodcon_logo">{{ brandingTitle }}</div>
            <h1 class="foodcon_title">한 번의 로그인으로 혜택 ON</h1>
            <p v-if="tenantDisplayName" class="foodcon_tenant_name">
                <span class="tenant_label">{{ tenantDisplayLabel }}</span>
                {{ tenantDisplayName }}
            </p>
            <p class="foodcon_desc">
                카카오 계정으로 빠르게 가입하고<br />
                할인 혜택을 만나보세요.
            </p>

            <button type="button" class="kakao_cta" @click="kakaoLoginBtn" id="kakaoLoginFoodcon">
                <img src="@/assets/images/icon/join_kakao.png" alt="카카오로 시작하기" />
            </button>

            <p class="foodcon_notice">※ 최초 가입 시 기업 고객 정보가 자동 연동됩니다.</p>
        </div>

        <AlertPopup v-if="showAlert" :title="alertTitle" :message="alertMessage" :showClose="true"
            @close="closeAlert" />

        <LayerPopup v-model="showMarketingConsentPopup" title="마케팅 정보 수신 동의">
            <template #layer_content>
                <div class="term_check_area">
                    <div class="d_check">
                        <input type="checkbox" v-model="marketingConsent.checked" @change="updateMarketingSubOptions"
                            id="foodcon-popup-marketing-all" />
                        <label for="foodcon-popup-marketing-all">[선택] 통합 마케팅 정보 수신 동의</label>
                    </div>

                    <div class="marketing_check_area">
                        <div class="d_check">
                            <input type="checkbox" v-model="marketingConsent.emailChecked"
                                id="foodcon-popup-marketing-email" @change="updateMarketingChecked" />
                            <label for="foodcon-popup-marketing-email">카카오톡</label>
                        </div>

                        <div class="d_check">
                            <input type="checkbox" v-model="marketingConsent.smsChecked"
                                id="foodcon-popup-marketing-sms" @change="updateMarketingChecked" />
                            <label for="foodcon-popup-marketing-sms">문자</label>
                        </div>

                        <p class="marketing_noti">하이비타민의 통합 혜택 및 이벤트 안내를 카카오톡/문자로 받아보실 수 있습니다.</p>
                    </div>

                    <div class="layer_term_area" v-html="marketingTermsContent"></div>

                    <div class="btn_login_area popup_actions">
                        <button type="button" class="btn_type2" @click="confirmMarketingConsent">동의하고 계속</button>
                    </div>
                </div>
            </template>
        </LayerPopup>
    </div>
</template>

<script>
import axios from "axios";
import AlertPopup from "@/components/AlertPopup.vue";
import LayerPopup from "@/components/LayerPopup.vue";
import { postAxios, getAxios, putAxios } from "@/lib/AxiosCall";
import { getKakaoAppKey, ensureKakaoInitialized } from "@/lib/SocialAuth";
import { encryptAES256 } from "@/lib/crypto";
import { SessionManager } from "@/lib/SessionManager";
import { LOCAL_STORAGE_HVM_USERINFO } from "@/lib/Constant";

const BENEFITTONG_ENT_UUIDS = new Set([
    "cbe43ba4-49b5-11f1-8f37-9cdc71ed3bda",
]);

export default {
    name: "HFO_LO_001_FOODCON",
    components: {
        AlertPopup,
        LayerPopup,
    },
    data() {
        return {
            isReady: false,
            showAlert: false,
            alertTitle: "알림",
            alertMessage: "",
            kakaoHandled: false,
            userData: {
                USER_ID: "",
                USER_NAME: "",
                USER_EMAIL: "",
                USER_BIRTH: "",
                USER_GENDER: "",
                USER_MOBILE: "",
                USER_IN_TYPE: "USER_IN_KAKAO",
            },
            showMarketingConsentPopup: false,
            marketingTermsContent: "",
            marketingConsent: {
                checked: true,
                emailChecked: true,
                smsChecked: true,
            },
            tenantDisplayName: "",
            tenantDisplayLabel: "",
            brandingTitle: "하이비타민X푸드콘",
            pendingMemberCheckAction: "",
            pendingMarketingUpdate: false,
        };
    },
    async mounted() {
        ensureKakaoInitialized();
        this.initTenantDisplayName();
        this.brandingTitle = this.resolveBrandingTitleByEntUuid();

        this.resumeMarketingFlowIfNeeded();

        const uuidParam = this.resolveTenantParam();
        if (uuidParam.value) {
            sessionStorage.setItem(uuidParam.storageKey, uuidParam.value);
        }

        this.saveCpsEntryContext();

        // 설계 기준: CPS 최초 진입 시 platformUser(=userId)로 CI를 선조회한다.
        const redirectedByPrecheck = await this.precheckMemberByPlatformUser();
        if (redirectedByPrecheck) {
            return;
        }

        const code = this.$route.query.code;
        if (code && !this.kakaoHandled) {
            this.handleKakaoCallback();
        } else {
            // precheck에서 리다이렉트되지 않고, 카카오 콜백도 아닌 경우 → 로그인 버튼 표시
            this.isReady = true;
        }
    },
    methods: {
        resolveBrandingTitleByEntUuid() {
            const normalizeUuid = (value) =>
                typeof value === "string" ? value.trim().toLowerCase() : "";

            const routeEntUuid = normalizeUuid(this.$route?.query?.entUuid);
            const tenant = this.resolveTenantParam();
            const tenantEntUuid =
                tenant.key === "entUuid" ? normalizeUuid(tenant.value) : "";
            const storedEntUuid = normalizeUuid(
                sessionStorage.getItem("foodcon_ent_uuid") ||
                sessionStorage.getItem("entUuid") ||
                localStorage.getItem("foodcon_ent_uuid") ||
                localStorage.getItem("entUuid") ||
                "",
            );

            const targetEntUuid = routeEntUuid || tenantEntUuid || storedEntUuid;

            if (BENEFITTONG_ENT_UUIDS.has(targetEntUuid)) {
                return "하이비타민X혜택통";
            }

            return "하이비타민X푸드콘";
        },
        decodeQueryValue(raw) {
            if (typeof raw !== "string") return "";
            try {
                return decodeURIComponent(raw);
            } catch (_e) {
                return raw;
            }
        },
        resolveTenantDisplayInfo() {
            const q = this.$route?.query || {};
            const queryEntName = this.decodeQueryValue(typeof q.entName === "string" ? q.entName : "").trim();
            const queryFrcName = this.decodeQueryValue(typeof q.frcName === "string" ? q.frcName : "").trim();

            try {
                const urlParams = new URLSearchParams(window.location.search);
                const urlEntName = this.decodeQueryValue(urlParams.get("entName") || "").trim();
                const urlFrcName = this.decodeQueryValue(urlParams.get("frcName") || "").trim();

                const entName = queryEntName || urlEntName || sessionStorage.getItem("foodcon_ent_name") || "";
                const frcName = queryFrcName || urlFrcName || sessionStorage.getItem("foodcon_frc_name") || "";

                if (entName) sessionStorage.setItem("foodcon_ent_name", entName);
                if (frcName) sessionStorage.setItem("foodcon_frc_name", frcName);

                if (frcName) return { label: "가맹점", name: frcName };
                if (entName) return { label: "기업", name: entName };
                return { label: "", name: "" };
            } catch (_e) {
                const entName = queryEntName || sessionStorage.getItem("foodcon_ent_name") || "";
                const frcName = queryFrcName || sessionStorage.getItem("foodcon_frc_name") || "";
                if (frcName) return { label: "가맹점", name: frcName };
                if (entName) return { label: "기업", name: entName };
                return { label: "", name: "" };
            }
        },
        initTenantDisplayName() {
            const info = this.resolveTenantDisplayInfo();
            this.tenantDisplayLabel = info.label;
            this.tenantDisplayName = info.name;
        },
        resolveTenantParam() {
            const q = this.$route.query || {};
            const byEnt = typeof q.entUuid === "string" ? q.entUuid : "";
            const byFrc = typeof q.frcUuid === "string" ? q.frcUuid : "";

            if (byEnt) {
                return { key: "entUuid", value: byEnt, storageKey: "foodcon_ent_uuid" };
            }
            if (byFrc) {
                return { key: "frcUuid", value: byFrc, storageKey: "foodcon_frc_uuid" };
            }

            try {
                const urlParams = new URLSearchParams(window.location.search);
                const urlFrc = urlParams.get("frcUuid") || "";
                if (urlFrc) {
                    return { key: "frcUuid", value: urlFrc, storageKey: "foodcon_frc_uuid" };
                }
            } catch (e) {
                // ignore
            }

            const storedEnt = sessionStorage.getItem("foodcon_ent_uuid") || "";
            if (storedEnt) {
                return { key: "entUuid", value: storedEnt, storageKey: "foodcon_ent_uuid" };
            }

            const storedFrc = sessionStorage.getItem("foodcon_frc_uuid") || "";
            if (storedFrc) {
                return { key: "frcUuid", value: storedFrc, storageKey: "foodcon_frc_uuid" };
            }

            const genericStoredFrc =
                sessionStorage.getItem("frcUuid") ||
                localStorage.getItem("foodcon_frc_uuid") ||
                localStorage.getItem("frcUuid") ||
                "";
            if (genericStoredFrc) {
                return { key: "frcUuid", value: genericStoredFrc, storageKey: "foodcon_frc_uuid" };
            }

            return { key: "", value: "", storageKey: "" };
        },
        resolveEntUuid() {
            const q = this.$route.query || {};
            const byQuery = typeof q.entUuid === "string" ? q.entUuid : "";
            if (byQuery) {
                sessionStorage.setItem("foodcon_ent_uuid", byQuery);
                sessionStorage.setItem("entUuid", byQuery);
                return byQuery;
            }

            try {
                const fromUrl = new URLSearchParams(window.location.search).get("entUuid") || "";
                if (fromUrl) {
                    sessionStorage.setItem("foodcon_ent_uuid", fromUrl);
                    sessionStorage.setItem("entUuid", fromUrl);
                    return fromUrl;
                }
            } catch (_e) {
                // ignore
            }

            return (
                sessionStorage.getItem("foodcon_ent_uuid") ||
                sessionStorage.getItem("entUuid") ||
                localStorage.getItem("foodcon_ent_uuid") ||
                localStorage.getItem("entUuid") ||
                ""
            );
        },
        resolveEntSeq() {
            const q = this.$route?.query || {};
            const toInt = (v) => {
                const n = Number(v);
                return Number.isInteger(n) && n > 0 ? n : 0;
            };

            const byQuery = toInt(q.entSeq);
            if (byQuery) {
                sessionStorage.setItem("foodcon_ent_seq", String(byQuery));
                sessionStorage.setItem("entSeq", String(byQuery));
                return byQuery;
            }

            try {
                const fromUrl = toInt(new URLSearchParams(window.location.search).get("entSeq"));
                if (fromUrl) {
                    sessionStorage.setItem("foodcon_ent_seq", String(fromUrl));
                    sessionStorage.setItem("entSeq", String(fromUrl));
                    return fromUrl;
                }
            } catch (_e) {
                // ignore
            }

            return toInt(
                sessionStorage.getItem("foodcon_ent_seq") ||
                sessionStorage.getItem("entSeq") ||
                localStorage.getItem("foodcon_ent_seq") ||
                localStorage.getItem("entSeq") ||
                ""
            );
        },
        async precheckMemberByPlatformUser() {
            try {
                const q = this.$route?.query || {};
                const entryType = String(q.entryType || "").toLowerCase();
                const platformUserRaw = typeof q.userId === "string" ? q.userId.trim() : "";

                if (entryType !== "cps" || !platformUserRaw) {
                    return false;
                }

                const encryptedPlatformUser = encryptAES256(platformUserRaw);
                const tenant = this.resolveTenantParam();
                const frcUuid = tenant.key === "frcUuid" ? tenant.value : "";
                const entSeq = this.resolveEntSeq();

                const res = await postAxios("/bgm/v1/foodcon/id", {
                    platformUser: encryptedPlatformUser,
                    ...(entSeq ? { entSeq } : {}),
                    ...(frcUuid ? { frcUuid } : {}),
                });

                const memberId = String((res && (res.id || res.ID)) || "").trim();
                const ci = (res && (res.CI || res.ci)) || "";
                if (ci) {
                    this.updateCpsEntryContext({ ci, userId: platformUserRaw });
                }

                // 회원 여부는 id 기준으로 판정하고, CI는 전달값으로만 보관한다.
                if (memberId) {
                    return this.finalizeCpsRedirect();
                }
            } catch (error) {
                console.warn("초기 platformUser CI 조회 실패:", error);
            }
            return false;
        },
        resolveOptionalPlatformParams() {
            let platformUser = "";
            let platformGroup = "";

            try {
                const query = this.$route?.query || {};
                const cpsRaw = sessionStorage.getItem("cps_entry_context") || localStorage.getItem("cps_entry_context");
                const cps = cpsRaw ? JSON.parse(cpsRaw) : {};

                platformUser = String(query.platformUser || query.userId || cps.userId || "").trim();
                platformGroup = String(query.platformGroup || query.subId || cps.subId || "").trim();
            } catch (_e) {
                platformUser = "";
                platformGroup = "";
            }

            const result = {};
            if (platformUser) result.platformUser = platformUser;
            if (platformGroup) result.platformGroup = platformGroup;
            return result;
        },
        getKakaoRedirectUri() {
            return `${window.location.origin}/HFO_LO_001_FOODCON`;
        },
        kakaoLoginBtn() {
            ensureKakaoInitialized();
            window.Kakao.Auth.authorize({
                redirectUri: this.getKakaoRedirectUri(),
                scope: "plusfriends",
            });
        },
        async handleKakaoCallback() {
            this.kakaoHandled = true;
            const code = this.$route.query.code;

            try {
                const tokenRes = await axios.post(
                    "https://kauth.kakao.com/oauth/token",
                    null,
                    {
                        params: {
                            grant_type: "authorization_code",
                            client_id: getKakaoAppKey(),
                            redirect_uri: this.getKakaoRedirectUri(),
                            code,
                        },
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded;charset=utf-8",
                        },
                    },
                );

                const accessToken = tokenRes?.data?.access_token || "";
                if (!accessToken) {
                    throw new Error("카카오 토큰 조회 실패");
                }

                window.Kakao.Auth.setAccessToken(accessToken);
                sessionStorage.setItem("kakao_access_token", accessToken);

                const profile = await window.Kakao.API.request({
                    url: "/v2/user/me",
                });

                if (!profile || !profile.id) {
                    throw new Error("카카오 프로필 조회 실패");
                }

                const acct = profile.kakao_account || {};
                this.userData.USER_ID = String(profile.id || "");
                this.userData.USER_MOBILE = acct.phone_number || "";
                this.userData.USER_NAME = acct.name || "";
                this.userData.USER_EMAIL = acct.email || "";
                this.userData.USER_BIRTH =
                    acct.birthday || (acct.birthyear ? `${acct.birthyear}-${acct.birthday}` : "");
                this.userData.USER_GENDER = acct.gender || "";
                this.userData.USER_IN_TYPE = "USER_IN_KAKAO";

                sessionStorage.setItem(LOCAL_STORAGE_HVM_USERINFO, JSON.stringify(this.userData));
                sessionStorage.setItem("userData", JSON.stringify(this.userData));

                await this.getMemberCheck(String(profile.id));
            } catch (err) {
                console.error("하이비타민X푸드콘 Kakao login error:", err);
                try {
                    if (window.Kakao?.Auth?.getAccessToken?.()) {
                        await window.Kakao.API.request({ url: "/v1/user/unlink" });
                        window.Kakao.Auth.setAccessToken(undefined);
                    }
                } catch (_e) {
                    // ignore
                }
                sessionStorage.removeItem("kakao_access_token");
                this.alertMessage = "카카오 로그인에 실패했습니다. 다시 시도해주세요.";
                this.showAlert = true;
                this.isReady = true;
            }
        },
        async getMemberCheck(id) {
            try {
                const encryptedId = encryptAES256(id);
                const tenant = this.resolveTenantParam();
                const frcUuid = tenant.key === "frcUuid" ? tenant.value : "";
                const entSeq = this.resolveEntSeq();
                const entUuid = this.resolveEntUuid();
                const entryType = String(this.$route?.query?.entryType || "").toLowerCase();
                const isCpsFlow = entryType === "cps";
                const platformParams = this.resolveOptionalPlatformParams();
                const platformUserRaw = String(platformParams.platformUser || "").trim();
                const encryptedPlatformUser = platformUserRaw ? encryptAES256(platformUserRaw) : encryptedId;
                const memberSearchApi = isCpsFlow ? "/bgm/v1/foodcon/id" : "/bgm/v1/search/id";
                const memberSearchPayload = isCpsFlow
                    ? {
                        platformUser: encryptedPlatformUser,
                        ...(entSeq ? { entSeq } : {}),
                        ...(frcUuid ? { frcUuid } : {}),
                    }
                    : {
                        userId: encryptedId,
                        type: "id",
                        ...(entUuid ? { entUuid } : {}),
                        ...(frcUuid ? { frcUuid } : {}),
                    };
                const res = await postAxios(memberSearchApi, memberSearchPayload);

                const memberId = String((res && (res.id || res.ID)) || "").trim();
                const ci = (res && (res.CI || res.ci)) || "";
                if (ci) {
                    this.updateCpsEntryContext({ ci });
                }

                // 공통 정책: 회원 여부(id) 기준으로 분기한다.
                if (!memberId) {
                    this.proceedToSignup();
                    return;
                }

                await this.handleLogin();
            } catch (error) {
                console.error("하이비타민X푸드콘 member check error:", error);
                this.alertMessage = "회원 확인 중 오류가 발생했습니다.";
                this.showAlert = true;
            }
        },
        async loadMarketingTermsContent() {
            if (this.marketingTermsContent) return;
            try {
                const resp = await getAxios("/hvm/v1/help/contract", {
                    contractType: "CONTRACT_MARKETING",
                });
                const content = resp?.page?.list?.[0]?.rmks || "";
                this.marketingTermsContent =
                    content ||
                    "<p>하이비타민의 통합 마케팅 정보 수신에 동의하시면 카카오톡/문자로 이벤트, 혜택, 프로모션 안내를 받아보실 수 있습니다.</p><p>주문/결제/계정 관련 필수 안내는 동의 여부와 관계없이 발송됩니다.</p>";
            } catch (error) {
                this.marketingTermsContent =
                    "<p>하이비타민의 통합 마케팅 정보 수신에 동의하시면 카카오톡/문자로 이벤트, 혜택, 프로모션 안내를 받아보실 수 있습니다.</p><p>주문/결제/계정 관련 필수 안내는 동의 여부와 관계없이 발송됩니다.</p>";
            }
        },
        updateMarketingSubOptions() {
            if (!this.marketingConsent.checked) {
                this.marketingConsent.emailChecked = false;
                this.marketingConsent.smsChecked = false;
            } else {
                this.marketingConsent.emailChecked = true;
                this.marketingConsent.smsChecked = true;
            }
        },
        updateMarketingChecked() {
            this.marketingConsent.checked =
                this.marketingConsent.emailChecked || this.marketingConsent.smsChecked;
        },
        resolveMarketingReturnPath() {
            const tenant = this.resolveTenantParam();
            if (tenant.key && tenant.value) {
                return `/HFO_LO_001_FOODCON?${tenant.key}=${encodeURIComponent(tenant.value)}`;
            }
            return "/HFO_LO_001_FOODCON";
        },
        async redirectToMarketingConsent(nextAction) {
            this.pendingMemberCheckAction = nextAction;
            this.pendingMarketingUpdate = true;
            await this.loadMarketingTermsContent();
            this.showMarketingConsentPopup = true;
        },
        async resumeMarketingFlowIfNeeded() {
            const isDone = sessionStorage.getItem("hvm_marketing_consent_done") === "Y";
            if (!isDone) return;

            const action = sessionStorage.getItem("hvm_marketing_next_action") || "";
            const needUpdate = sessionStorage.getItem("hvm_marketing_update_pending") === "Y";

            sessionStorage.removeItem("hvm_marketing_consent_done");
            sessionStorage.removeItem("hvm_marketing_next_action");
            sessionStorage.removeItem("hvm_marketing_return_to");
            sessionStorage.removeItem("hvm_marketing_update_pending");

            this.pendingMarketingUpdate = needUpdate;

            if (action === "signup") {
                this.proceedToSignup();
                return;
            }
            if (action === "login") {
                await this.handleLogin();
            }
        },
        async confirmMarketingConsent() {
            sessionStorage.setItem(
                "emailChecked",
                this.marketingConsent.emailChecked ? "true" : "false",
            );
            sessionStorage.setItem(
                "smsChecked",
                this.marketingConsent.smsChecked ? "true" : "false",
            );

            this.showMarketingConsentPopup = false;
            if (this.pendingMemberCheckAction === "signup") {
                this.proceedToSignup();
                return;
            }
            await this.handleLogin();
        },
        proceedToSignup() {
            const uuidParam = this.resolveTenantParam();
            if (uuidParam.value) {
                sessionStorage.setItem(uuidParam.storageKey, uuidParam.value);
            }

            const cpsContext = this.getCpsEntryContext();
            const query = uuidParam.key && uuidParam.value
                ? { [uuidParam.key]: uuidParam.value }
                : {};
            if (cpsContext) {
                const hasTenantTarget = !!(query.entUuid || query.frcUuid || cpsContext.entUuid || cpsContext.frcUuid);
                query.entryType = cpsContext.entryType || (hasTenantTarget ? "qr" : "cps");
                if (cpsContext.targetUrl) query.targetUrl = cpsContext.targetUrl;
                if (cpsContext.clickId) query.clickId = cpsContext.clickId;
                if (cpsContext.adId) query.adId = cpsContext.adId;
                if (cpsContext.subId) query.subId = cpsContext.subId;
                if (cpsContext.ci) query.ci = cpsContext.ci;
                if (cpsContext.entUuid && !query.entUuid) query.entUuid = cpsContext.entUuid;
                if (cpsContext.frcUuid && !query.frcUuid) query.frcUuid = cpsContext.frcUuid;
            }

            this.$router.push({
                path: "/HFO_MB_004_SNS",
                query,
            });
        },
        buildTenantJoinCompleteQuery(tenant) {
            const info = this.resolveTenantDisplayInfo();
            const query = { joinType: "tenant-link", source: "foodcon-crm" };

            if (tenant?.key && tenant?.value) {
                query[tenant.key] = tenant.value;
            }

            if (info?.label === "가맹점" && info?.name) {
                query.frcName = info.name;
            } else if (info?.label === "기업" && info?.name) {
                query.entName = info.name;
            }

            return query;
        },
        async handleLogin() {
            try {
                const stored = JSON.parse(sessionStorage.getItem(LOCAL_STORAGE_HVM_USERINFO) || "{}");
                const id = encryptAES256(String(stored.USER_ID || this.userData.USER_ID || ""));
                const tenant = this.resolveTenantParam();
                const frcUuid = tenant.key === "frcUuid" ? tenant.value : "";
                const entSeq = this.resolveEntSeq();
                const entUuid = this.resolveEntUuid();
                const entryType = String(this.$route?.query?.entryType || "").toLowerCase();
                const isCpsFlow = entryType === "cps";

                const platformParams = this.resolveOptionalPlatformParams();
                const loginPlatformPayload = {
                    ...(platformParams.platformUser
                        ? { platformUser: encryptAES256(String(platformParams.platformUser).trim()) }
                        : {}),
                    ...(platformParams.platformGroup
                        ? { platformGroup: platformParams.platformGroup }
                        : {}),
                };

                const resp = await postAxios("/bgm/v1/mem/login", {
                    userId: id,
                    userPwd: "",
                    userInType: "USER_IN_KAKAO",
                    ...loginPlatformPayload,
                    ...(isCpsFlow && entSeq ? { entSeq } : {}),
                    ...(!isCpsFlow && entUuid ? { entUuid } : {}),
                    ...(frcUuid ? { frcUuid } : {}),
                });

                if (resp?.errors) {
                    throw new Error("로그인 실패");
                }

                localStorage.setItem(LOCAL_STORAGE_HVM_USERINFO, JSON.stringify(resp));

                if (this.pendingMarketingUpdate && resp?.userUuid) {
                    try {
                        const emailYn = this.marketingConsent.emailChecked ? "Y" : "N";
                        const smsYn = this.marketingConsent.smsChecked ? "Y" : "N";
                        await putAxios(`bgm/v1/mem/member/${resp.userUuid}`, {
                            emailYn,
                            smsYn,
                        });
                        resp.emailYn = emailYn;
                        resp.smsYn = smsYn;
                        localStorage.setItem(LOCAL_STORAGE_HVM_USERINFO, JSON.stringify(resp));
                    } catch (error) {
                        console.warn("마케팅 동의 저장 실패:", error);
                    } finally {
                        this.pendingMarketingUpdate = false;
                    }
                }
                SessionManager.startSession();

                if (this.finalizeCpsRedirect()) {
                    return;
                }

                if (!isCpsFlow && tenant?.value) {
                    this.$router.push({
                        path: "/HFO_MB_005",
                        query: this.buildTenantJoinCompleteQuery(tenant),
                    });
                    return;
                }

                const redirect = this.$route.query.redirect || "/";
                this.$router.push({ path: redirect });
            } catch (error) {
                console.error("하이비타민X푸드콘 login error:", error);
                this.alertMessage = "로그인에 실패했습니다. 다시 시도해주세요.";
                this.showAlert = true;
            }
        },
        saveCpsEntryContext() {
            const q = this.$route?.query || {};
            const tenant = this.resolveTenantParam();
            const existing = this.getCpsEntryContext() || {};

            const entUuid = typeof q.entUuid === "string" && q.entUuid
                ? q.entUuid
                : (tenant.key === "entUuid" && tenant.value ? tenant.value : (existing.entUuid || this.resolveEntUuid()));
            const frcUuid = typeof q.frcUuid === "string" && q.frcUuid
                ? q.frcUuid
                : (tenant.key === "frcUuid" && tenant.value ? tenant.value : (existing.frcUuid || ""));
            const fallbackEntryType = entUuid || frcUuid ? "qr" : "cps";

            const queryEntryType = typeof q.entryType === "string" ? q.entryType : "";
            const queryUserId = typeof q.userId === "string" ? q.userId : "";
            const queryTargetUrl = typeof q.targetUrl === "string" ? q.targetUrl : "";
            const queryClickId = typeof q.clickId === "string" ? q.clickId : "";
            const queryAdId = typeof q.adId === "string" ? q.adId : "";
            const querySubId = typeof q.subId === "string" ? q.subId : "";
            const queryCi = typeof q.ci === "string" ? q.ci : "";

            const context = {
                entryType: queryEntryType || existing.entryType || fallbackEntryType,
                userId: queryUserId || existing.userId || "",
                entUuid,
                frcUuid,
                targetUrl: queryTargetUrl || existing.targetUrl || "",
                clickId: queryClickId || existing.clickId || "",
                adId: queryAdId || existing.adId || "",
                subId: querySubId || existing.subId || "",
                ci: queryCi || existing.ci || "",
                createdAt: existing.createdAt || new Date().toISOString(),
            };
            const serialized = JSON.stringify(context);
            sessionStorage.setItem("cps_entry_context", serialized);
            localStorage.setItem("cps_entry_context", serialized);
        },
        updateCpsEntryContext(patch) {
            const current = this.getCpsEntryContext() || {};
            const merged = {
                ...current,
                ...patch,
                createdAt: current.createdAt || new Date().toISOString(),
            };
            const serialized = JSON.stringify(merged);
            sessionStorage.setItem("cps_entry_context", serialized);
            localStorage.setItem("cps_entry_context", serialized);
        },
        getCpsEntryContext() {
            try {
                const raw = sessionStorage.getItem("cps_entry_context") || localStorage.getItem("cps_entry_context");
                return raw ? JSON.parse(raw) : null;
            } catch (_e) {
                return null;
            }
        },
        finalizeCpsRedirect() {
            const cpsContext = this.getCpsEntryContext();
            const targetUrl = (cpsContext && cpsContext.targetUrl) ||
                (typeof this.$route.query.targetUrl === "string" ? this.$route.query.targetUrl : "");
            if (!targetUrl) {
                return false;
            }

            let finalUrl = targetUrl;
            const ci = (cpsContext && cpsContext.ci) || (typeof this.$route.query.ci === "string" ? this.$route.query.ci : "");
            const entUuid = String(
                (typeof this.$route.query.entUuid === "string" ? this.$route.query.entUuid : "") ||
                (cpsContext && cpsContext.entUuid) ||
                sessionStorage.getItem("foodcon_ent_uuid") ||
                sessionStorage.getItem("entUuid") ||
                localStorage.getItem("foodcon_ent_uuid") ||
                localStorage.getItem("entUuid") ||
                ""
            ).trim();
            const frcUuid = String(
                (typeof this.$route.query.frcUuid === "string" ? this.$route.query.frcUuid : "") ||
                (cpsContext && cpsContext.frcUuid) ||
                sessionStorage.getItem("foodcon_frc_uuid") ||
                sessionStorage.getItem("frcUuid") ||
                localStorage.getItem("foodcon_frc_uuid") ||
                localStorage.getItem("frcUuid") ||
                ""
            ).trim();
            if (ci) {
                try {
                    const parsed = new URL(finalUrl);
                    parsed.searchParams.set("ci", ci);
                    const isFoodconWelcome = /foodcon\.co\.kr$/i.test(parsed.hostname) &&
                        /^\/welcome(?:\.html)?$/i.test(parsed.pathname);
                    if (isFoodconWelcome && !parsed.searchParams.get("entUuid") && !parsed.searchParams.get("frcUuid")) {
                        if (entUuid) {
                            parsed.searchParams.set("entUuid", entUuid);
                        } else if (frcUuid) {
                            parsed.searchParams.set("frcUuid", frcUuid);
                        }
                    }
                    finalUrl = parsed.toString();
                } catch (_e) {
                    // ignore malformed URL and fallback to original
                }
            } else {
                try {
                    const parsed = new URL(finalUrl);
                    const isFoodconWelcome = /foodcon\.co\.kr$/i.test(parsed.hostname) &&
                        /^\/welcome(?:\.html)?$/i.test(parsed.pathname);
                    if (isFoodconWelcome && !parsed.searchParams.get("entUuid") && !parsed.searchParams.get("frcUuid")) {
                        if (entUuid) {
                            parsed.searchParams.set("entUuid", entUuid);
                        } else if (frcUuid) {
                            parsed.searchParams.set("frcUuid", frcUuid);
                        }
                    }
                    finalUrl = parsed.toString();
                } catch (_e) {
                    // ignore malformed URL and fallback to original
                }
            }

            sessionStorage.removeItem("cps_entry_context");
            localStorage.removeItem("cps_entry_context");
            window.location.href = finalUrl;
            return true;
        },
        closeAlert() {
            this.showAlert = false;
        },
    },
};
</script>

<style scoped>
.foodcon_page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 18px;
    box-sizing: border-box;
    background:
        radial-gradient(circle at top right, rgba(255, 201, 56, 0.25), transparent 40%),
        linear-gradient(180deg, #fff8e8 0%, #fff 40%, #fff 100%);
}

.foodcon_page.is-loading {
    background: #fff;
}

.foodcon_card {
    width: 100%;
    max-width: 420px;
    background: #ffffff;
    border-radius: 20px;
    padding: 28px 20px 24px;
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.08);
    border: 1px solid #f2f2f2;
    text-align: center;
}

.foodcon_logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 10px;
    margin-bottom: 10px;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
    color: #8a5b00;
    background: #fff1bf;
}

.foodcon_title {
    margin: 0;
    font-size: 24px;
    line-height: 1.35;
    font-weight: 800;
    color: #191919;
}

.foodcon_desc {
    margin: 10px 0 18px;
    color: #555;
    font-size: 14px;
    line-height: 1.45;
}

.foodcon_tenant_name {
    margin: 8px 0 2px;
    color: #2f2f2f;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    word-break: keep-all;
}

.tenant_label {
    display: inline-block;
    margin-right: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: #7a5600;
    background: #ffefc2;
    vertical-align: middle;
}

.kakao_cta {
    width: 100%;
    border: none;
    background: transparent;
    padding: 0;
    margin: 2px 0 10px;
    cursor: pointer;
    border-radius: 10px;
    overflow: hidden;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.kakao_cta.mini {
    max-width: 220px;
    margin: 0 auto;
}

.popup_actions {
    margin-top: 18px;
}

.btn_login_area .btn_type2 {
    width: 100%;
    height: 56px;
    line-height: 56px;
    background: #ee7528;
    color: #fff;
    border: none;
    border-radius: 5px;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    display: block;
}

.btn_login_area .btn_type2:hover {
    background: #d66520;
}

.kakao_cta img {
    display: block;
    width: 100%;
    height: auto;
}

.kakao_cta:active {
    transform: scale(0.985);
}

.foodcon_notice {
    margin: 0;
    color: #777;
    font-size: 12px;
    line-height: 1.4;
}

@media (max-width: 480px) {
    .foodcon_page {
        padding: 16px 14px;
    }

    .foodcon_card {
        padding: 22px 14px 20px;
        border-radius: 16px;
    }

    .foodcon_title {
        font-size: 21px;
    }
}

.foodcon_loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
}

.foodcon_spinner {
    display: inline-block;
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #009ca6;
    border-radius: 50%;
    animation: foodcon-spin 0.8s linear infinite;
}

@keyframes foodcon-spin {
    to {
        transform: rotate(360deg);
    }
}

.foodcon_loading_text {
    font-size: 18px;
    color: #374151;
    font-weight: 500;
    margin: 0;
}

.foodcon_loading_sub {
    font-size: 14px;
    color: #9ca3af;
    margin: 0;
}
</style>
